import { DocumentType, index, modelOptions, mongoose, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { LOCALES, type Locale } from "../../i18n/constants.js";
import {
    ENTITY_NAME_MAX_LENGTH,
    entityNameZodValidator,
    ObjectIdString,
    StoredVerdiIdString,
} from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { MessagingChannelAddress } from "../commonSubSchemas/messagingChannelAddress.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    OrganizationInvitationFieldsWithDefaults,
    OrganizationInvitationSchemaTypes,
} from "../types/organizationInvitation.d.js";
import { DtoValidatorKeys, type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "organizationInvitations" as const;

const Sha256HexString = z.string().regex(/^[0-9a-f]{64}$/);

export enum OrganizationInvitationKind {
    NEW_USER = "NEW_USER",
    EXISTING_USER = "EXISTING_USER",
}

/** Expiry is not a status: a PENDING invitation past `expiresAt` is expired. */
export enum OrganizationInvitationStatus {
    PENDING = "PENDING",
    ACCEPTED = "ACCEPTED",
    REVOKED = "REVOKED",
}

type KindDependentFields = Partial<Pick<OrganizationInvitation, "kind" | "invitedUserId" | "name" | "locale">>;

/** EXISTING_USER carries `invitedUserId` and no starting values; NEW_USER the reverse. */
function kindFieldIssues(invitation: KindDependentFields): { field: keyof KindDependentFields; message: string }[] {
    if (invitation.kind === undefined) return [];
    const isPresent = (field: keyof KindDependentFields) =>
        invitation[field] !== undefined && invitation[field] !== null;
    const existingUser = invitation.kind === OrganizationInvitationKind.EXISTING_USER;

    const issues: { field: keyof KindDependentFields; message: string }[] = [];
    if (existingUser !== isPresent("invitedUserId")) {
        issues.push({ field: "invitedUserId", message: "invitedUserId is required for EXISTING_USER only" });
    }
    if (existingUser) {
        (["name", "locale"] as const)
            .filter(isPresent)
            .forEach((field) => issues.push({ field, message: `${field} is for NEW_USER invitations only` }));
    }
    return issues;
}

/** An offer of membership in one organization, sent to a channel address. SCD Type-2: a resend is a new version. */
@index({ tokenHash: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@index(
    { organizationId: 1, status: 1, "channelAddress.channel": 1, "channelAddress.address": 1 },
    { partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "organizationInvitations" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@pre<OrganizationInvitation>("validate", function (this: DocumentType<OrganizationInvitation>) {
    kindFieldIssues(this).forEach(({ field, message }) => this.invalidate(field, message));
})
@post<SchemaBase<OrganizationInvitation>>(...addSchemaKeyHook(SCHEMA_KEY))
export class OrganizationInvitation extends ScdDocument<
    OrganizationInvitation,
    OrganizationInvitationFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** The organization's verdiId. */
    @validate({ zodValidator: StoredVerdiIdString, responseOnly: true })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public organizationId!: string;

    @validate({ childClass: MessagingChannelAddress })
    @prop({ type: () => MessagingChannelAddress, required: true, _id: false })
    public channelAddress!: MessagingChannelAddress;

    @validate({ zodValidator: z.enum(OrganizationInvitationKind), responseOnly: true })
    @prop({ type: () => String, required: true, enum: OrganizationInvitationKind })
    public kind!: OrganizationInvitationKind;

    /** The invited user account's verdiId. */
    @validate({ zodValidator: StoredVerdiIdString.optional(), responseOnly: true })
    @prop({ type: () => String, required: false, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public invitedUserId?: string;

    @validate({ zodValidator: entityNameZodValidator.optional() })
    @prop({ type: () => String, required: false, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name?: string;

    @validate({ zodValidator: z.enum(LOCALES).optional() })
    @prop({ type: () => String, required: false, enum: LOCALES })
    public locale?: Locale;

    /** SHA-256 hex of the token, never the raw token. No @validate: never in a DTO. */
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(Sha256HexString) })
    public tokenHash!: string;

    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public expiresAt!: Date;

    @validate({ zodValidator: z.enum(OrganizationInvitationStatus), responseOnly: true })
    @prop({
        type: () => String,
        required: true,
        enum: OrganizationInvitationStatus,
        default: OrganizationInvitationStatus.PENDING,
    })
    public status: OrganizationInvitationStatus = OrganizationInvitationStatus.PENDING;

    /** The accepting user account's verdiId. */
    @validate({ zodValidator: StoredVerdiIdString.optional(), responseOnly: true })
    @prop({ type: () => String, required: false, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public acceptedByUserId?: string;

    static getters = {} as const satisfies Record<string, (self: OrganizationInvitation) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<OrganizationInvitationSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<OrganizationInvitation, OrganizationInvitationFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

OrganizationInvitation.dtoValidators = getZodSchemaFromClass<OrganizationInvitation>(OrganizationInvitation, {
    superRefine: (invitation, ctx, variant) => {
        if (variant === DtoValidatorKeys.Update) return;
        kindFieldIssues(invitation).forEach(({ field, message }) =>
            ctx.addIssue({ code: "custom", message, path: [field] }),
        );
    },
}) as unknown as DtoValidatorTypes<OrganizationInvitationSchemaTypes>;
